import { useCallback, useEffect, useRef } from "react";
import { getPlatform } from "@/platform";
import { largeFileUploader } from "@/clients/large-file.plugin";
import { attachFilePickerEvents, limitImportFiles } from "@/file-picker";
import { createActionError } from "@/client-error";

export interface SelectedFile {
  name: string;
  size: number;
  type: string;
  path: string;
}

interface WebFilePickerState {
  dispose: () => void;
  input: HTMLInputElement;
  pendingResolve: ((files: File[]) => void) | null;
}

const isFilePickerCancellation = (error: unknown) =>
  error instanceof Error && /cancel/i.test(error.message);

const ensureSupportedFileType = (
  fileName: string,
  supportedExtensions?: string[],
) => {
  if (!supportedExtensions?.length) {
    return;
  }

  const normalizedName = fileName.toLowerCase();
  const isSupported = supportedExtensions.some((extension) =>
    normalizedName.endsWith(extension.toLowerCase()),
  );

  if (!isSupported) {
    throw createActionError("UnsupportedFile");
  }
};

const useFileSelector = () => {
  const webFilePickerRef = useRef<WebFilePickerState | null>(null);

  const disposeWebFilePicker = useCallback(() => {
    const webFilePicker = webFilePickerRef.current;

    if (!webFilePicker) {
      return;
    }

    webFilePicker.dispose();
    webFilePickerRef.current = null;
  }, []);

  const getWebFilePicker = useCallback(() => {
    if (webFilePickerRef.current) {
      return webFilePickerRef.current;
    }

    const input = document.createElement("input");
    const webFilePicker: WebFilePickerState = {
      dispose: () => {},
      input,
      pendingResolve: null,
    };

    const finish = (selectedFiles: File[]) => {
      const pendingResolve = webFilePicker.pendingResolve;

      if (!pendingResolve) {
        return;
      }

      webFilePicker.pendingResolve = null;
      input.value = "";
      input.accept = "";
      input.multiple = false;
      pendingResolve(selectedFiles);
    };

    const handleChange = () => {
      finish(Array.from(input.files ?? []));
    };

    const handleCancel = () => {
      finish([]);
    };

    input.type = "file";
    input.tabIndex = -1;
    input.setAttribute("aria-hidden", "true");
    Object.assign(input.style, {
      height: "1px",
      left: "0",
      opacity: "0",
      pointerEvents: "none",
      position: "fixed",
      top: "0",
      width: "1px",
    });

    const detachInputEvents = attachFilePickerEvents(input, {
      change: handleChange,
      cancel: handleCancel,
    });
    document.body.appendChild(input);

    webFilePicker.dispose = () => {
      if (webFilePicker.pendingResolve) {
        webFilePicker.pendingResolve([]);
        webFilePicker.pendingResolve = null;
      }

      detachInputEvents();

      if (document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    webFilePickerRef.current = webFilePicker;
    return webFilePicker;
  }, []);

  useEffect(() => {
    return () => {
      disposeWebFilePicker();
    };
  }, [disposeWebFilePicker]);

  const openFilesPicker = useCallback(
    async (
      supportedExtensions?: string[],
      maxFiles: number = 1,
    ): Promise<Array<File | SelectedFile>> => {
      if (["android", "ios"].includes(getPlatform())) {
        try {
          const result = await largeFileUploader.pickFile({
            mimeType: "*/*",
            allowMultiple: maxFiles > 1,
          });

          if (!result) {
            return [];
          }

          const selectedFiles = limitImportFiles(
            "files" in result ? result.files : [result],
            maxFiles,
          )
            .map(
              (file): SelectedFile => ({
                name: file.name || file.displayName || "file",
                size: file.size || 0,
                type: file.mimeType || "application/octet-stream",
                path: file.uri,
              }),
            );

          selectedFiles.forEach((file) =>
            ensureSupportedFileType(file.name, supportedExtensions),
          );
          return selectedFiles;
        } catch (error) {
          if (isFilePickerCancellation(error)) {
            return [];
          }
          throw error;
        }
      }

      if (typeof document === "undefined") {
        return [];
      }

      const webFilePicker = getWebFilePicker();
      const files = await new Promise<File[]>((resolve) => {
        if (webFilePicker.pendingResolve) {
          webFilePicker.pendingResolve([]);
        }

        webFilePicker.pendingResolve = resolve;

        // Reset before every click so selecting the same file still emits
        // `change`, and only resolve from explicit file input events.
        const { input } = webFilePicker;
        input.value = "";
        input.accept = supportedExtensions?.length
          ? supportedExtensions.join(",")
          : "";
        input.multiple = maxFiles > 1;
        input.click();
      });

      const selectedFiles = limitImportFiles(files, maxFiles);
      selectedFiles.forEach((file) => {
        ensureSupportedFileType(file.name, supportedExtensions);
      });

      return selectedFiles;
    },
    [getWebFilePicker],
  );

  return {
    openFilesPicker,
  };
};

export default useFileSelector;
